import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import {
  AccordionItem,
  AccordionMode,
  Accordion,
} from '@hotelplan/components.common.accordion';
import { Icon } from '@hotelplan/components.common.icon';
import { mapFeatureNameToIcon } from '@hotelplan/libs.mappers';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { useCommonFeatureGroups } from 'fdr/components/candidate/fdr-wishlist/fdr-use-common-feature-groups';
import { TFdrWishlistCompareItem } from './fdr-wishlist-compare-items.types';
import { getFeatureIconAndColorForCompareItem } from './fdr-wishlist.utils';

export interface IWishlistItemCompareFeaturesProps {
  item: TFdrWishlistCompareItem | null;
}

const AccordionItemWrapper = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    flex: 1,
    fontSize: 2,
    justifyContent: 'space-between',
    alignItems: 'center',
    '.accordion-icon': {
      verticalAlign: 'text-top',
      mr: [2, '10px'],
      width: '16px',
      height: '16px',
    },
  })
);

const FeaturesList = styled.ul(
  sx2CssThemeFn({
    padding: 3,
  })
);

const FeatureItem = styled.li(
  sx2CssThemeFn({
    display: 'flex',
    '> div': {
      flexShrink: 0,
    },
    '&:not(:last-child)': {
      mb: 3,
    },
    '.wishlist-feature': {
      ml: -2,
      mr: 1,
      fontSize: 1,
    },
    '.wishlist-feature-name': {
      flex: 1,
    },
  })
);

const FeatureIconWrapper = styled.div<{ color: string }>(
  ({ color, theme, theme: { space, colors } }) => ({
    lineHeight: '1',
    marginRight: space[2],
    '.wishlist-feature-icon': {
      ...theme.icons.lg,
      color: color === 'error' ? colors.error : colors.success,
    },
  })
);

const FdrWishlistCompareItemFeatures: React.FC<IWishlistItemCompareFeaturesProps> =
  ({ item }) => {
    const [t] = useTranslation(['wishlist']);
    const commonFeatureGroups = useCommonFeatureGroups();

    return (
      <Accordion mode={AccordionMode.MULTIPLE}>
        {commonFeatureGroups.map(({ id, name, features }, i) => {
          return (
            <AccordionItem
              key={`feature-group-${i}`}
              title={
                <AccordionItemWrapper>
                  <span>
                    <Icon
                      name={mapFeatureNameToIcon(id)}
                      className="accordion-icon"
                    />
                    {name}
                  </span>
                </AccordionItemWrapper>
              }
            >
              <FeaturesList>
                {features.map((feature, idx) => {
                  const productFeatureIcon =
                    item.descriptionFeatureGroups
                      .find(fg => fg.id === id)
                      ?.features.find(f => f.id === feature.id)?.icon ||
                    getFeatureIconAndColorForCompareItem(feature, item);

                  return (
                    <FeatureItem key={`feature-${idx}`}>
                      {productFeatureIcon && (
                        <>
                          {productFeatureIcon.isNA && (
                            <div className="wishlist-feature">
                              {t('wishlist:featureNA.title')}
                            </div>
                          )}
                          <FeatureIconWrapper color={productFeatureIcon.color}>
                            <Icon
                              name={productFeatureIcon.name}
                              className="wishlist-feature-icon"
                            />
                          </FeatureIconWrapper>
                        </>
                      )}
                      <div className="wishlist-feature-name">
                        {feature.name}
                      </div>
                    </FeatureItem>
                  );
                })}
              </FeaturesList>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

export default FdrWishlistCompareItemFeatures;
